import { useContext, useEffect, useState } from 'react';
import { UserContext } from '../../components/shared/useUser';
import { useTranslation } from 'react-i18next';
import { CreditRequestRag, PosFeedLastReceived, PurchaseOrderRag, RAG, StockCheckRAG } from '../../app/services/api/generated';
import { Box, Grid, useTheme } from '@mui/material';
import { RAGDetail } from './types/RAGDetail';
import { RAGRecord } from './types/RAGRecord';
import { CreditRequestRAGRecord } from './types/CreditRequestRAGRecord';
import { ConfigurationName } from '../../enums/ConfigurationName';
import { CreditRequestRAGDetail } from './types/CreditRequestRAGDetail';
import useNswagClient from '../../hooks/api/useNswagClient';
import useLogError from '../../hooks/useLogError';
import TitleArea from './components/TitleArea';
import StockCount from './components/StockCount';
import DailyTasks from './components/DailyTasks';
import ActualTheoryReportsTable from './components/ActualTheoryReportsTable';
import ValueOfCreditReportsTable from './components/ValueOfCreditReportsTable';
import CreditRequestsDetails from './components/CreditRequestsDetails';
import Notifications from './components/Notifications';
import OperationalDayBanner from './components/OperationalDayBanner';
import { System } from '../../enums/System';
import { useConfigurations } from '../../components/shared/useConfigurations';
import { getDisplayStoreNumberByInternal } from '../../utils/storeNumber';

const DashboardPage = () => {
  const { getStockCheckByRagStatus, getStockCheckRag, getPurchaseOrdersRag, getTransactionsRag, getTransactionsByRagStatus, getPurchaseOrdersByRagStatus, getCreditRequestStatusRag, getCreditRequestStatusDataByRagStatus } = useNswagClient();
  const { isConfigurationEnabled } = useConfigurations();
  const { selectedStore, selectedSystem, stores } = useContext(UserContext);
  const { user, hasPermissionTo } = useContext(UserContext);
  const { logError } = useLogError();
  const theme = useTheme();

  const [stockDetail, setStockDetail] = useState<RAGDetail>({});
  const [porDetail, setPorDetail] = useState<RAGDetail>({});
  const [posDetail, setPosDetail] = useState<RAGDetail>({});
  const [creditRequestStatusDetail, setCreditRequestStatusDetail] = useState<CreditRequestRAGDetail>({});

  const [stockCheckRag, setStockCheckRag] = useState<RAG | undefined>(undefined);
  const [posRag, setPosRag] = useState<RAG | undefined>(undefined);
  const [porRag, setPorRag] = useState<RAG | undefined>(undefined);
  const [creditRequestStatusRag, setCreditRequestStatusRag] = useState<RAG | undefined>(undefined);
  const isCreditRequestEnabled = isConfigurationEnabled(ConfigurationName.CreditRequestsEnabled);
  const { t } = useTranslation('common');
  const isComplianceSystem = selectedSystem?.id === System.OrderlyForCompliance;


  //Roles.PurchaseOrderRead,
  //Roles.StockCountRead
  //Roles.TransactionRead

  useEffect(() => {
    if (hasPermissionTo(['StockCountRead'])) {
      getStockCheckRag()
        .then((data) => {
          setStockCheckRag(data.data);

          if (data.data?.red ?? 0 > 0) {
            getStockCheckByRagStatus(true, false, false)
              .then((data) => {
                const detail = stockDetail;
                detail.Red = mapStockChecks(data.data);
                setStockDetail(detail);
              })
              .catch((e) => logError(e));
          } else {
            const detail = stockDetail;
            detail.Red = mapStockChecks(undefined);
            setStockDetail(detail);
          }

          if (data.data?.amber ?? 0 > 0) {
            getStockCheckByRagStatus(false, true, false)
              .then((data) => {
                const detail = stockDetail;
                detail.Orange = mapStockChecks(data.data);
                setStockDetail(detail);
              })
              .catch((e) => logError(e));
          } else {
            const detail = stockDetail;
            detail.Orange = mapStockChecks(undefined);
            setStockDetail(detail);
          }

          if (data.data?.green ?? 0 > 0) {
            getStockCheckByRagStatus(false, false, true)
              .then((data) => {
                const detail = stockDetail;
                detail.Green = mapStockChecks(data.data);
                setStockDetail(detail);
              })
              .catch((e) => logError(e));
          } else {
            const detail = stockDetail;
            detail.Green = mapStockChecks(undefined);
            setStockDetail(detail);
          }

        })
        .catch((e) => logError(e));
    }

    if (hasPermissionTo(['PurchaseOrderRead'])) {

      getPurchaseOrdersRag()
        .then((data) => {
          setPorRag(data.data);

          if (data.data?.red ?? 0 > 0) {
            getPurchaseOrdersByRagStatus(1)
              .then((data) => {
                const detail = porDetail;
                detail.Red = mapPurchaseOrders(data.data);
                setPorDetail(detail);
              })
              .catch((e) => logError(e));
          } else {
            const detail = porDetail;
            detail.Red = mapPurchaseOrders(undefined);
            setPorDetail(detail);
          }

          if (data.data?.amber ?? 0 > 0) {
            getPurchaseOrdersByRagStatus(2)
              .then((data) => {
                const detail = porDetail;
                detail.Orange = mapPurchaseOrders(data.data);
                setPorDetail(detail);
              })
              .catch((e) => logError(e));
          } else {
            const detail = porDetail;
            detail.Orange = mapPurchaseOrders(undefined);
            setPorDetail(detail);
          }

          if (data.data?.green ?? 0 > 0) {
            getPurchaseOrdersByRagStatus(3)
              .then((data) => {
                const detail = porDetail;
                detail.Green = mapPurchaseOrders(data.data);
                setPorDetail(detail);
              })
              .catch((e) => logError(e));
          } else {
            const detail = porDetail;
            detail.Green = mapPurchaseOrders(undefined);
            setPorDetail(detail);
          }

        })
        .catch((e) => logError(e));
    }

    if (hasPermissionTo(['TransactionRead'])) {
      getTransactionsRag()
        .then((data) => {
          setPosRag(data.data);

          if (data.data?.red ?? 0 > 0) {
            getTransactionsByRagStatus(1)
              .then((data) => {
                const detail = posDetail;
                detail.Red = mapTransactions(data.data);
                setPosDetail(detail);
              })
              .catch((e) => logError(e));
          } else {
            const detail = posDetail;
            detail.Red = mapTransactions(undefined);
            setPosDetail(detail);
          }

          if (data.data?.amber ?? 0 > 0) {
            getTransactionsByRagStatus(2)
              .then((data) => {
                const detail = posDetail;
                detail.Orange = mapTransactions(data.data);
                setPosDetail(detail);
              })
              .catch((e) => logError(e));
          } else {
            const detail = posDetail;
            detail.Orange = mapTransactions(undefined);
            setPosDetail(detail);
          }

          if (data.data?.green ?? 0 > 0) {
            getTransactionsByRagStatus(3)
              .then((data) => {
                const detail = posDetail;
                detail.Green = mapTransactions(data.data);
                setPosDetail(detail);
              })
              .catch((e) => logError(e));
          } else {
            const detail = posDetail;
            detail.Green = mapTransactions(undefined);
            setPosDetail(detail);
          }
        })
        .catch((e) => logError(e));
    }

    if (hasPermissionTo(['PurchaseOrderRead'])) {
      getCreditRequestStatusRag(selectedStore?.storeNumber ?? '')
        .then((data) => {
          setCreditRequestStatusRag(data.data);

          if (data.data?.red || 0 > 0) {
            getCreditRequestStatusDataByRagStatus(1, selectedStore?.storeNumber ?? '')
              .then((data) => {
                const detail = creditRequestStatusDetail;
                detail.Red = mapCreditRequests(data.data);
                setCreditRequestStatusDetail(detail);
              })
              .catch((e) => logError(e));
          } else {
            const detail = creditRequestStatusDetail;
            detail.Red = mapCreditRequests(undefined);
            setCreditRequestStatusDetail(detail);
          }

          if (data.data?.amber || 0 > 0) {
            getCreditRequestStatusDataByRagStatus(2, selectedStore?.storeNumber ?? '')
              .then((data) => {
                const detail = creditRequestStatusDetail;
                detail.Orange = mapCreditRequests(data.data);
                setCreditRequestStatusDetail(detail);
              })
              .catch((e) => logError(e));
          } else {
            const detail = creditRequestStatusDetail;
            detail.Orange = mapCreditRequests(undefined);
            setCreditRequestStatusDetail(detail);
          }

          if (data.data?.green || 0 > 0) {
            getCreditRequestStatusDataByRagStatus(3, selectedStore?.storeNumber ?? '')
              .then((data) => {
                const detail = creditRequestStatusDetail;
                detail.Green = mapCreditRequests(data.data);
                setCreditRequestStatusDetail(detail);
              })
              .catch((e) => logError(e));
          } else {
            const detail = creditRequestStatusDetail;
            detail.Green = mapCreditRequests(undefined);
            setCreditRequestStatusDetail(detail);
          }
        })
        .catch((e) => logError(e));
    }


  }, []);

  const mapTransactions = (data: PosFeedLastReceived[] | undefined): RAGRecord[] => {
    if (data) {
      const ragDetails = data.map((transaction) => {
        return {
          id: transaction.storeName,
          name: '',
          storeName: transaction.storeName,
          itemDate: transaction.date,
        };
      });
      ragDetails.forEach((rag, index) => rag.id = (index + 1).toString());
      return ragDetails;
    } else {
      return [];
    }
  };

  const mapStockChecks = (data: StockCheckRAG[] | undefined): RAGRecord[] => {
    if (data) {
      return data.map((stockCheck) => {
        return {
          id: stockCheck.id?.toString(),
          name: stockCheck.name,
          storeName: stockCheck.storeName,
          itemDate: stockCheck.plannedCountDate,
        };
      });
    } else {
      return [];
    }
  };

  const mapCreditRequests = (data: CreditRequestRag[] | undefined): CreditRequestRAGRecord[] => {
    if (data) {
      return data.map((creditRequestCheck) => {
        return {
          storeName: creditRequestCheck.storeName?.toString(),
          storeNumber: getDisplayStoreNumberByInternal(stores, creditRequestCheck.storeNumber?.toString()),
          orderNumber: creditRequestCheck.orderNumber?.toString(),
          creditedAmount: creditRequestCheck.creditedAmount?.toString(),
        };
      });
    } else {
      return [];
    }
  };

  const mapPurchaseOrders = (data: PurchaseOrderRag[] | undefined): RAGRecord[] => {
    if (data) {
      return data.map((po) => {
        return {
          id: (po.orderNo ?? '') + (po.type ?? ''),
          name: '',
          storeName: po.store,
          itemDate: po.dateOfReceipt,
          orderNumber: po.orderNo,
          orderType: po.type,
        };
      });
    } else {
      return [];
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexGrow: 1,
        flexDirection: 'column',
        minHeight: '100vh',
        maxWidth: '100%',
      }}
    >
      <TitleArea storeName={selectedStore?.storeName ?? ''}></TitleArea>
      <OperationalDayBanner storeNumber={selectedStore?.storeNumber}
        franchiseName={selectedStore?.franchiseName}></OperationalDayBanner>
      <Box
        sx={{
          py: 3,
          mb: isComplianceSystem ? 0 : 5,
          flexGrow: 1,
          backgroundImage: `url(${theme.brandingImages.dashboardImage})`,
          backgroundSize: 'cover',
          backgroundAttachment: 'fixed',
        }}
      >
        <Box sx={{ maxWidth: { xs: '100%', lg: '100%' }, m: { xs: '30px 12px 18px', lg: '50px' } }}>

          <Grid container
            spacing={5}>
            <Grid item
              xs={12}
              lg={6}>
              <DailyTasks storeNumber={selectedStore?.storeNumber} ></DailyTasks>
            </Grid>
            <Grid item
              xs={12}
              lg={6}>
              <Notifications storeNumber={selectedStore?.storeNumber} />
            </Grid>
          </Grid>

        </Box>
      </Box>
      {!isComplianceSystem && (
        <Box display={'flex'}
          flexDirection={'row'}
          flexWrap={'wrap'}
        >
          <Box
            sx={{
              width: {
                xs: 1,
                lg: 1 / 2,
              },
            }}
          >
            {hasPermissionTo(['StockCountRead']) ?
              <StockCount
                title={t('stockCountTitle')}
                redStatusGroup={t('stockCountRedGroup')}
                orangeStatusGroup={t('stockCountOrangeGroup')}
                greenStatusGroup={t('stockCountGreenGroup')}
                ragDetail={stockDetail}
                greenNumber={stockCheckRag?.green}
                redNumber={stockCheckRag?.red}
                orangeNumber={stockCheckRag?.amber}
              ></StockCount>
              : null}
            {hasPermissionTo(['TransactionRead']) ?
              <StockCount
                title={t('posTitle')}
                redStatusGroup={t('posRedGroup')}
                orangeStatusGroup={t('posOrangeGroup')}
                greenStatusGroup={t('posGreenGroup')}
                ragDetail={posDetail}
                greenNumber={posRag?.green}
                redNumber={posRag?.red}
                orangeNumber={posRag?.amber}
              ></StockCount> : null}
            {hasPermissionTo(['PurchaseOrderRead']) ?
              <StockCount
                title={t('porsTitle')}
                redStatusGroup={t('porsRedGroup')}
                orangeStatusGroup={t('porsOrangeGroup')}
                greenStatusGroup={t('porsGreenGroup')}
                ragDetail={porDetail}
                greenNumber={porRag?.green}
                redNumber={porRag?.red}
                orangeNumber={porRag?.amber}
              ></StockCount> : null}
            {
              isCreditRequestEnabled && hasPermissionTo(['PurchaseOrderRead']) && (
                <CreditRequestsDetails
                  title={t('creditRequestStatusTitle')}
                  redStatusGroup={t('creditRequestStatusRedGroup')}
                  orangeStatusGroup={t('creditRequestStatusOrangeGroup')}
                  greenStatusGroup={t('creditRequestStatussGreenGroup')}
                  ragDetail={creditRequestStatusDetail}
                  redNumber={creditRequestStatusRag?.red}
                  orangeNumber={creditRequestStatusRag?.amber}
                  greenNumber={creditRequestStatusRag?.green}
                ></CreditRequestsDetails>
              )
            }
          </Box>
          <Box
            sx={{
              width: {
                xs: 1,
                lg: 1 / 2,
              },
            }}
          >
            {
              (user?.username?.includes('@orderly.io') || user?.emailAddress?.includes('@orderly.io')) && <ActualTheoryReportsTable />
            }

            {
              isCreditRequestEnabled && hasPermissionTo(['PurchaseOrderRead']) &&
              <ValueOfCreditReportsTable />
            }
          </Box>
        </Box>
      )}
    </Box >
  );
};

export default DashboardPage;
